import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Modeling time in React",
  "description": "",
  "published": "2016-11-09T00:00:00.000Z",
  "redirect_from": ["/blog/modeling-time-react/swizec/7200"],
  "categories": "react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/clock-example.gif",
        "alt": null
      }}></img></p>
    <p>{`Ever wanted to build a timer in React? It's a tricky thing.`}</p>
    <p>{`Ideally, React components rely only on their inputs – the props – so that they're testable, reusable, and easier to understand. Time is the ultimate state, that global dependency that just won't sit still.`}</p>
    <p>{`Calling `}<inlineCode parentName="p">{`new Date()`}</inlineCode>{` or `}<inlineCode parentName="p">{`moment()`}</inlineCode>{` to get the current time in your component makes it dirty. You can't test because it throws unpredictable results depending on system context. You can't reuse and re-render either. Damn thing keeps changing!`}</p>
    <p><em parentName="p">{`And`}</em>{` you need some mechanism to keep refreshing that time state if you want your component to show something live. You can add a `}<inlineCode parentName="p">{`setInterval`}</inlineCode>{` that runs `}<inlineCode parentName="p">{`this.setState(moment())`}</inlineCode>{` every second, and that's going to work. But if you show multiple timers on screen, they're eventually going to diverge and look weird.`}</p>
    <h2 {...{
      "id": "crib-from-systemhardware-design"
    }}>{`Crib from system/hardware design`}</h2>
    <p>{`Those problems have all been solved already in electronics and operating systems: it’s `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Clock_signal"
      }}>{`the clock signal`}</a>{` in electronics and `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/System_time"
      }}>{`system time`}</a>{` in operating systems. Both reduce time to a stateful resource that everyone shares.`}</p>
    <p>{`The idea is this:`}</p>
    <ul>
      <li parentName="ul">{`make a `}<inlineCode parentName="li">{`Clock`}</inlineCode>{` store`}</li>
      <li parentName="ul">{`emit an action every `}<inlineCode parentName="li">{`period`}</inlineCode>{` milliseconds`}</li>
      <li parentName="ul">{`share `}<inlineCode parentName="li">{`Clock`}</inlineCode>{` with everyone`}</li>
    </ul>
    <p>{`With this approach, time becomes just another prop passed or injected into components that need it. Components themselves can treat it like an immutable value, which makes them easy to test, and because all components rely on the same `}<inlineCode parentName="p">{`setInterval`}</inlineCode>{`, they're never going to fall out of sync.`}</p>
    <p>{`Let me show you.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/clock-actions.gif",
        "alt": null
      }}></img></p>
    <p>{`I'm going to use MobX, but the same approach should work with Redux and even the classical state-from-main-component-callbacks-up-components approach.`}</p>
    <p>{`You can see `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/mobx-clock-example"
      }}>{`the code on Github`}</a>{`.`}</p>
    <h2 {...{
      "id": "clock-store"
    }}>{`Clock store`}</h2>
    <p>{`First, we need a `}<inlineCode parentName="p">{`Clock`}</inlineCode>{` store. It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import { observable, action } from "mobx";
import moment from "moment";

class Clock {
  @observable time = moment();

  constructor(period = 1000) {
    this.interval = setInterval(() => this.clockTick(), period);
  }

  @action clockTick(newTime = moment()) {
    this.time = newTime;
  }
}

const clock = new Clock();

export default clock;
`}</code></pre>
    <p>{`We have an `}<inlineCode parentName="p">{`@observable`}</inlineCode>{` `}<inlineCode parentName="p">{`time`}</inlineCode>{` property, which means MobX is going to communicate changes to any `}<inlineCode parentName="p">{`observer`}</inlineCode>{`s. The `}<inlineCode parentName="p">{`constructor`}</inlineCode>{` starts an interval with the specified period. In our case, this is going to be a 1Hz clock updating once per second.`}</p>
    <p>{`We store the interval in `}<inlineCode parentName="p">{`this.interval`}</inlineCode>{` so that we can stop it. This implementation doesn't allow the clock to be stopped or reset, but it would be easy to add an action for that.`}</p>
    <p>{`Each tick of the clock triggers an `}<inlineCode parentName="p">{`@action`}</inlineCode>{` called `}<inlineCode parentName="p">{`clockTick`}</inlineCode>{`, which updates the `}<inlineCode parentName="p">{`time`}</inlineCode>{` property to the current time. MobX is smart enough to let us naively replace it with a new instance of `}<inlineCode parentName="p">{`moment()`}</inlineCode>{`, and I'm sure Redux would be too.`}</p>
    <p>{`The real trick to making this work is in the last two lines:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const clock = new Clock();

export default clock;
`}</code></pre>
    <p>{`We export `}<em parentName="p">{`an instance of Clock`}</em>{`, not the class. This ensures that anywhere we use `}<inlineCode parentName="p">{`import Clock`}</inlineCode>{`, we get the same singleton.`}</p>
    <p><em parentName="p">{`"But inject()! You can inject() and that will make a singleton!”`}</em>{` I hear you thinking.`}</p>
    <p>{`Yes, but you can only `}<inlineCode parentName="p">{`inject()`}</inlineCode>{` into React components. Sometimes `}<inlineCode parentName="p">{`MobX`}</inlineCode>{` stores need access to The Clock as well. You can't inject into those, but you `}<em parentName="p">{`can`}</em>{` import stuff.`}</p>
    <h2 {...{
      "id": "time-display"
    }}>{`Time display`}</h2>
    <p><inlineCode parentName="p">{`Clock`}</inlineCode>{` was the fun part. Let me show you that it's easy to use :)`}</p>
    <p>{`We make a `}<inlineCode parentName="p">{`Time*`}</inlineCode>{` component that displays durations. It can be a functional stateless component that takes `}<inlineCode parentName="p">{`Clock`}</inlineCode>{` from React contexts via `}<inlineCode parentName="p">{`inject()`}</inlineCode>{` and renders a thing.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const CurrentTime = inject("Clock")(
  observer(({ Clock }) => (
    <div>
      Current Time: <duration d={Clock.time}></duration>
    </div>
  ))
);
`}</code></pre>
    <p>{`Observes changes on `}<inlineCode parentName="p">{`Clock`}</inlineCode>{` state and gets `}<inlineCode parentName="p">{`Clock`}</inlineCode>{` from the context with `}<inlineCode parentName="p">{`inject`}</inlineCode>{`. You could inject a static object with a `}<inlineCode parentName="p">{`time = N`}</inlineCode>{` property when testing.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`Duration`}</inlineCode>{` component takes a `}<inlineCode parentName="p">{`moment()`}</inlineCode>{` object and displays it as a digital clock. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const Duration = ({ d }) => {
  let h = d.hours(),
    m = d.minutes(),
    s = d.seconds();

  [h, m, s] = [h, m, s].map((n) => (n < 10 ? \`0\${n}\` : n));

  return (
    <code>
      {h}:{m}:{s}
    </code>
  );
};
`}</code></pre>
    <p>{`Take `}<inlineCode parentName="p">{`hours`}</inlineCode>{`/`}<inlineCode parentName="p">{`minutes`}</inlineCode>{`/`}<inlineCode parentName="p">{`seconds`}</inlineCode>{` from the object, and prefix with a `}<inlineCode parentName="p">{`0`}</inlineCode>{` when each is smaller than `}<inlineCode parentName="p">{`10`}</inlineCode>{`. Yes, this breaks for negative values. It looks like this: `}<inlineCode parentName="p">{`0-1:0-4:05`}</inlineCode>{`.`}</p>
    <p>{`But that shouldn't be hard to fix ?`}</p>
    <p>{`In `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/mobx-clock-example"
      }}>{`the full code`}</a>{`, I built a generalized `}<inlineCode parentName="p">{`Time`}</inlineCode>{` displays that defers to `}<inlineCode parentName="p">{`TimeSince`}</inlineCode>{`, `}<inlineCode parentName="p">{`TimeUntil`}</inlineCode>{`, and `}<inlineCode parentName="p">{`CurrentTime`}</inlineCode>{` as necessary. Their code is almost identical, so I didn't want to explain it all here.`}</p>
    <h2 {...{
      "id": "putting-it-together-in-appjs"
    }}>{`Putting it together in App.js`}</h2>
    <p>{`Now that you have a system `}<inlineCode parentName="p">{`Clock`}</inlineCode>{` and a way to show `}<inlineCode parentName="p">{`Time`}</inlineCode>{`, you can put it all together inside your `}<inlineCode parentName="p">{`App`}</inlineCode>{` like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class App extends Component {
  render() {
      return (
          <provider clock={Clock}>
              <div class="App">
                  <devtools>
                  <div class="App-intro">
                      <time until="{moment().add(10," 'minutes')}="">
                      <time since="{moment().subtract(5," 'minutes')}="">
                      <time>
                  </time></time></time></div>
              </devtools></div>
          </provider>
    );
  }
}
`}</code></pre>
    <p><inlineCode parentName="p">{`Provider`}</inlineCode>{` puts the `}<inlineCode parentName="p">{`Clock`}</inlineCode>{` singleton in React context. This gives all your components access to the `}<inlineCode parentName="p">{`Clock`}</inlineCode>{` via `}<inlineCode parentName="p">{`inject()`}</inlineCode>{` and you can do anything you want with it.`}</p>
    <p>{`Cool, isn't it?`}</p>
    <p><strong parentName="p">{`And yes, it works in stores, too.`}</strong></p>
    <p>{`Check this out from my Day Job code.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`MobX lets you do really cool things. when() might just be my fav feature. `}<a parentName="p" {...{
          "href": "https://t.co/nGDwmsOJ2c"
        }}>{`pic.twitter.com/nGDwmsOJ2c`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/795798444094853121"
      }}>{`November 8, 2016`}</a></blockquote>
    <p>{`It's in a store, `}<inlineCode parentName="p">{`isOutOfTime`}</inlineCode>{` is a `}<inlineCode parentName="p">{`@computed`}</inlineCode>{` property that compares `}<inlineCode parentName="p">{`Clock.time`}</inlineCode>{` to some pre-defined deadline. `}<inlineCode parentName="p">{`when()`}</inlineCode>{` the deadline passes, an `}<inlineCode parentName="p">{`@action`}</inlineCode>{` is called.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    @computed get timeLeft() {
        if (this.should_end_by) {
            return moment.duration(
                this.should_end_by
                                  .diff(Clock.time)
                          );
        }else{
            return null;
        }
    }

    @computed get isOutOfTime() {
        return !_.isNull(this.timeLeft) && this.timeLeft <= 0;
    }
`}</code></pre>
    <p>{`Isn't that cool? I think it's cool. The code is easy to understand, easy to test, no messing around with intervals and relying on slippery system properties. Things just work™.`}</p>
    <p>{`It's almost like your codebase is in a time monad ?`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      